import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { UserManager, User, WebStorageStateStore } from 'oidc-client-ts';

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
  getAccessToken: () => string | undefined;
  userProfile: UserProfile | null;
}

interface UserProfile {
  sub: string;
  email: string;
  name: string;
  roles: Record<string, string>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

// OIDC configuration — can be overridden via environment variables at build time.
const oidcConfig = {
  authority: process.env.REACT_APP_OIDC_ISSUER || 'https://auth.secuaas.com',
  client_id: process.env.REACT_APP_OIDC_CLIENT_ID || '361461795539452169',
  redirect_uri: window.location.origin + '/',
  post_logout_redirect_uri: window.location.origin + '/',
  response_type: 'code',
  scope: 'openid profile email urn:zitadel:iam:org:project:id:359880428426887433:aud urn:zitadel:iam:org:project:roles',
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userManager] = useState(() => new UserManager(oidcConfig));

  const loadUser = useCallback(async () => {
    try {
      // Handle OIDC callback.
      if (window.location.search.includes('code=')) {
        const u = await userManager.signinRedirectCallback();
        setUser(u);
        window.history.replaceState({}, '', window.location.pathname);
        setIsLoading(false);
        return;
      }

      const u = await userManager.getUser();
      if (u && !u.expired) {
        setUser(u);
      }
    } catch (err) {
      console.error('Auth error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [userManager]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // Fetch user profile from API when we have a token.
  useEffect(() => {
    if (!user?.access_token) {
      setUserProfile(null);
      return;
    }
    fetch('/api/v1/auth/me', {
      headers: { Authorization: `Bearer ${user.access_token}` },
    })
      .then((r) => r.json())
      .then(setUserProfile)
      .catch(console.error);
  }, [user?.access_token]);

  const login = useCallback(() => {
    userManager.signinRedirect();
  }, [userManager]);

  const logout = useCallback(() => {
    userManager.signoutRedirect();
  }, [userManager]);

  const getAccessToken = useCallback(() => {
    return user?.access_token;
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user && !user.expired,
        login,
        logout,
        getAccessToken,
        userProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
